import styled from 'styled-components';
import React, { ChangeEvent, FC, Fragment, MouseEventHandler, useRef, useState } from 'react';
import { Paper } from '@material-ui/core';
import { TFunction, useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { clone, cloneDeep, filter, find, forEach, isArray, isEmpty, isEqual, map } from 'lodash-es';
import { Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
    AddBluePulseIcon,
    BasicBadge,
    BasicInput,
    ContactSvg,
    Container,
    DialogBody,
    InfoPointIcon,
    palette,
    PrimaryButton,
    SimpleToggle,
    TabsPanel,
    TertiaryButton,
    ToggleButtonGroup,
    Typography,
    Upselling,
    UpsellingBody,
} from '@dev/ee-components';
import { generateIdBasedOnPath, safelyDecodeBase64Url } from '@dev/ee-tools';
import { ContactField, ContactFieldType, ContactsList, Segment, SegmentPayload } from '@dev/ee-api/ee-api-models';
import { push } from 'redux-first-history';

import { ShowDialog } from '@/store/dialog/actions';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { SegmentsState } from '@/store/segments/types';
import { ListsState } from '@/store/lists/types';
import { CustomFieldsInterface } from '@/store/customFields/types';
import { ShowDialogParams } from '@/store/dialog/types';
import {
    SegmentAddStart,
    SegmentClearRuleEvaluationResult,
    SegmentRuleEvaluate,
    SegmentUpdateEdited,
} from '@/store/segments/actions';
import { getPaymentsUrl } from '@/services/tools/getPaymentsUrl';
import { getAccountStore } from '@/store/account/actions';
import { useSecurity } from '@/services/hooks/useSecurity';

import SegmentRuleRow, { arrayRuleToString, parseSingleRule, parseStringRule } from '../SegmentRuleTool';
import { CreateSegmentState } from './CreateSegmentInterface';
import {
    OptionInterface,
    RuleConditionType,
    RuleConditionTypeToConditionOptions,
    RuleFieldOptionInterface,
} from '../SegmentRuleTool/SegmentRuleToolInterface';
import { AndOrOptions, ConditionOptions, RuleFieldOptions, RuleValueOptions } from '../SegmentRuleTool/selectOptions';
import { getDataFromStore, useInitData } from './Hooks';

/* eslint-disable max-lines-per-function */
/* eslint-disable no-shadow */
const RuleSpan = styled.span`
    color: ${palette.gray[5]};
    font-size: 14px;
    font-family: Mulish;
`;

const TrackHistoryToggleWrapper = styled.div`
    width: 64px;
    padding-top: 5px;
    display: flex;
    align-items: center;
`;

const TrackHistoryToggleRow = styled.div`
    display: flex;
    align-items: center;
`;

const InfoBlock = styled.div`
    align-items: center;
    background: ${palette.indigo[1]};
    border-radius: 4px;
    display: flex;
    padding: 16px;
`;

const RulesWrapper = styled.div`
    margin: 32px 0;
`;

const RuleWrapper = styled.div`
    margin-bottom: 16px;
`;

const AddRuleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const AddRuleBtn = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

const EvaluateWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    min-height: 30px;
`;

const RuleEvaluationResultSpan = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 24px;
    text-transform: capitalize;
`;

const useStyles = makeStyles(() => ({
    paperRoot: {
        marginTop: '32px',
        padding: '32px 48px',
        border: `1px solid ${palette.gray[3]}`,
        '&.segmentName': {
            marginTop: 0,
        },
    },
    ruleTypographyRoot: {
        marginTop: '24px',
        display: 'inline-block',
    },
    ruleNotificationTypograpyRoot: {
        color: palette.gray[7],
        marginLeft: '16px',
    },
    dividerRoot: {
        backgroundColor: palette.gray[2],
        margin: '25px 0',
    },
    andOrRadio: {
        textTransform: 'uppercase' as const,
    },
}));

const SEGMENT_RULE_INDENTATION = 30;
const NEW_RULE_STRING = `${RuleFieldOptions.segmentrulelabelgeneral[0].value} ${
    ConditionOptions['String' as string][0].value
} ''`;

type HandlerFunction = (event?: ChangeEvent<HTMLInputElement>, value?: any) => void;
type VoidFunction = () => void;
type RadioChangeHandler = (event: any, value?: string) => void;
type ChangeRuleHandler = (rule: string) => void;

/**
 * Updates rule at given position with passed value
 * @param parsedRule - rule string parsed to array
 * @param position - array consisting of rule coordinates
 * @param value - new value of rule
 */
const updateRuleValue = (parsedRule, position, value): void => {
    if (isArray(parsedRule[position[0]])) {
        // Get to clicked row level
        updateRuleValue(parsedRule[position[0]], position.slice(1), value);
    } else {
        // Change all condition operators on level if and/or changed
        if (value === 'AND' || value === 'OR') {
            forEach(parsedRule, (rulePart: string, index): void => {
                if (rulePart === 'AND' || rulePart === 'OR') {
                    parsedRule[index] = value;
                }
            });
        } else {
            // update
            parsedRule[position[0]] = value;
        }
    }
};

type RuleConditionOperator = 'AND' | 'OR';

const getLevelConditionOperator = (parsedRuleLevel: (string | string[])[]): RuleConditionOperator => {
    let operator: RuleConditionOperator;
    forEach(parsedRuleLevel, (ruleItem): boolean => {
        if (ruleItem === 'AND' || ruleItem === 'OR') {
            operator = ruleItem;
            return false;
        }
    });
    return operator;
};

/**
 * Updates passed rule at given position with a new rule on deeper level
 * @param parsedRule - rule string parsed to array
 * @param position - array consisting of rule coordinates
 */

const addSubRule = (parsedRule: (string | string[])[], position: number[]): void => {
    let operator: RuleConditionOperator;
    if (position.length > 1) {
        // Get to clicked row level
        addSubRule(parsedRule[position[0]] as string[], position.slice(1));
    } else {
        if (isArray(parsedRule[position[0] + 2])) {
            operator = getLevelConditionOperator(parsedRule[position[0] + 2] as string[]);
            // if current row has subrules
            (parsedRule[position[0] + 2] as string[]).push(operator || 'OR');
            (parsedRule[position[0] + 2] as string[]).push(NEW_RULE_STRING);
        } else {
            operator = getLevelConditionOperator(parsedRule);
            // if current row has not yet any subrules
            (parsedRule as (string | string[])[]).splice(position[0] + 1, 0, operator || 'OR', [NEW_RULE_STRING]);
        }
    }
};

/**
 * Updates passed rule at given position with a new rule on the same level
 * @param parsedRule - rule string parsed to array
 * @param position - array consisting of rule coordinates
 */

const addRuleUnder = (parsedRule: (string | string[])[], position: number[]): void => {
    let operator: RuleConditionOperator;
    if (position.length > 1) {
        // Get to clicked row level
        addRuleUnder(parsedRule[position[0]] as string[], position.slice(1));
    } else {
        operator = getLevelConditionOperator(parsedRule);
        if (isArray(parsedRule[position[0] + 2])) {
            // if current row has subrules add new rule after subrules
            (parsedRule as (string | string[])[]).splice(position[0] + 3, 0, operator || 'OR', NEW_RULE_STRING);
        } else {
            // if current row has not subrules
            (parsedRule as (string | string[])[]).splice(position[0] + 1, 0, operator || 'OR', NEW_RULE_STRING);
        }
    }
};

/**
 * Updates passed rule by removing a subrule at given position
 * @param parsedRule - rule string parsed to array
 * @param position - array consisting of rule coordinates from deepest to top layer ex. []
 */
const removeRule = (parsedRule: (string | string[])[], position): boolean => {
    let isRuleAloneOnLevel;

    // Check if there's a lower level to get into
    switch (true) {
        case position.length < 2: {
            // we're at 1st level in the rule (deepest one)
            position[0] === 0
                ? // If rule to remove is first on it's level
                  // Erase rule and next element ("AND"/"OR")
                  (parsedRule as string[]).splice(0, 2)
                : // Erase rule and previous element ("AND"/"OR")
                  (parsedRule as string[]).splice(position[0] - 1, 2);

            return false;
        }
        case position.length === 2:
            // we're at 2nd level in the rule (counting from deepest)
            if (parsedRule[position[0]].length === 1 && parsedRule.length === 1) {
                // clicked rule is the only on its level
                // remove whole  parsedRule[position[1]]
                (parsedRule as string[]).splice(position[0], 1);
                return true;
            } else if (parsedRule[position[0]].length === 1) {
                removeRule(parsedRule, [position[0]]);
            } else {
                removeRule(parsedRule[position[0]] as string[], position.slice(1));
            }
            break;
        case position.length > 2:
            if (isArray(parsedRule[position[0]])) {
                // Get to clicked row parent level
                isRuleAloneOnLevel = removeRule(parsedRule[position[0]] as (string | string[])[], position.slice(1));
                if (isRuleAloneOnLevel && parsedRule.length === 1) {
                    (parsedRule as string[]).splice(position[0], 1);
                    return true;
                } else if (isRuleAloneOnLevel) {
                    removeRule(parsedRule, [position[0]]);
                }
            }
            break;
    }
};

/**
 * Each row's "add subrule" click handler
 * @param rulePosition - rule's position in parsedRule array coordinates
 * @param setState - CreateSegment's setState
 */
const handleAddSubruleClick =
    (rulePosition: number[], setState: any): VoidFunction =>
    (): void => {
        setState((currentState): CreateSegmentState => {
            const clonedStateRule = cloneDeep(currentState.parsedRule);

            addSubRule(clonedStateRule, rulePosition);

            return {
                ...currentState,
                parsedRule: clonedStateRule,
                rule: arrayRuleToString(clonedStateRule),
            };
        });
    };

/**
 * Each row's "add condition" click handler
 * @param rulePosition - rule's position in parsedRule array coordinates
 * @param setState - CreateSegment's setState
 */
const handleAddRuleUnderClick =
    (rulePosition: number[], setState: any): VoidFunction =>
    (): void => {
        setState((currentState): CreateSegmentState => {
            const clonedStateRule = cloneDeep(currentState.parsedRule);

            addRuleUnder(clonedStateRule, rulePosition);

            return {
                ...currentState,
                parsedRule: clonedStateRule,
                rule: arrayRuleToString(clonedStateRule),
            };
        });
    };

/**
 * Each row's "Delete" button click handler
 * @param rulePosition - rule's position in parsedRule array coordinates
 * @param setState - CreateSegment's setState
 */
const handleRemoveRuleClick =
    (rulePosition: number[], setState: any): VoidFunction =>
    (): void => {
        setState((currentState): CreateSegmentState => {
            const clonedStateRule = cloneDeep(currentState.parsedRule);

            removeRule(clonedStateRule, rulePosition);

            return {
                ...currentState,
                parsedRule: clonedStateRule,
                rule: arrayRuleToString(clonedStateRule),
            };
        });
    };

/**
 * Handler attached to every row's change event
 * @param rulePosition - rule's position in parsedRule array coordinates
 * @param setState - CreateSegment's setState
 */
const handleChangeRuleClick =
    (rulePosition: number[], setState: any): ChangeRuleHandler =>
    (rule: string): void => {
        setState((currentState): CreateSegmentState => {
            const clonedStateRule = cloneDeep(currentState.parsedRule);

            updateRuleValue(clonedStateRule, rulePosition, rule);

            return {
                ...currentState,
                parsedRule: clonedStateRule,
                rule: arrayRuleToString(clonedStateRule),
            };
        });
    };

/**
 * Change handler for "AND / OR" radio between rules
 * @param rulePosition - rule's position in parsedRule array coordinates
 * @param setState - CreateSegment's setState
 */
const handleChangeOrAndRadio =
    (rulePosition: number[], setState: any): RadioChangeHandler =>
    (value: string): void => {
        setState((currentState): void => {
            const clonedStateRule = cloneDeep(currentState.parsedRule);

            updateRuleValue(clonedStateRule, rulePosition, value);

            return {
                ...currentState,
                parsedRule: clonedStateRule,
                rule: arrayRuleToString(clonedStateRule),
            };
        });
    };

/**
 * Render rule rows & count errored rows & set state.errorPositions
 * @param parsedRule - rule string parsed to array
 * @param setState - setState function of CreateSegment component
 * @param props - CreateSegment props
 * @param state - CreateSegment state
 */
const ruleRows = (
    parsedRule: (string | string[])[],
    setState: any,
    andOrRadioClass: string,
    state: CreateSegmentState
): JSX.Element[] => {
    const errorPositions = [];

    /**
     * Recursively walks rule by rule to render rows
     */
    const renderRuleRows = (
        parsedRule: (string | string[])[],
        depthLevel = 0,
        position: number[] = []
    ): JSX.Element[] => {
        const currentPosition = clone(position);
        currentPosition.push(0);
        return map(parsedRule, (ruleElement, index): JSX.Element => {
            currentPosition[currentPosition.length - 1] = index;
            if (typeof ruleElement !== 'string') {
                return (
                    <div
                        key={`rule-wrapper-level-${index}-${depthLevel}--${currentPosition.join('-')}`}
                        style={{ fontSize: '24px', lineHeight: '1' }}
                    >
                        <span style={{ marginLeft: '-3px' }}>(</span>
                        <div
                            style={{
                                margin: '4px 0 0 2px',
                                borderLeft: '1px solid #E6E9F2',
                                paddingLeft: `${SEGMENT_RULE_INDENTATION}px`,
                                paddingTop: '2px',
                            }}
                        >
                            {renderRuleRows(ruleElement, depthLevel + 1, currentPosition)}
                        </div>
                        {')'}
                    </div>
                );
            }

            const rulePosition = clone(currentPosition);
            let ruleObject;
            switch (ruleElement) {
                case 'AND':
                case 'OR':
                    return (
                        <div
                            style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}
                            key={`segment-rule-${ruleElement}-${index}-${depthLevel}--${rulePosition.join('-')}`}
                        >
                            <ToggleButtonGroup
                                id={`condition-button-${index}`}
                                onChange={handleChangeOrAndRadio(rulePosition, setState)}
                                options={AndOrOptions}
                                value={ruleElement}
                                classes={{ root: andOrRadioClass }}
                            />
                        </div>
                    );
                default:
                    ruleObject = parseSingleRule(ruleElement);
                    if (ruleObject?.ruleField?.isNotFieldFound) {
                        errorPositions.push(rulePosition.join(','));
                    }
                    return (
                        <SegmentRuleRow
                            key={`segment-rule-row--${rulePosition.join('-')}`}
                            index={index}
                            position={rulePosition.join(',')}
                            rule={ruleElement}
                            ruleObject={ruleObject}
                            onChangeRuleFieldSelect={handleChangeRuleClick(rulePosition, setState)}
                            onClickAddSubrule={handleAddSubruleClick(rulePosition, setState)}
                            onClickAddRule={handleAddRuleUnderClick(rulePosition, setState)}
                            onClickRemoveSubrule={handleRemoveRuleClick(rulePosition, setState)}
                        />
                    );
            }
        });
    };

    const rows = renderRuleRows(parsedRule);

    if (!isEqual(state.errorPositions, errorPositions)) {
        setState({
            ...state,
            errorPositions,
        });
    }
    return rows;
};

/**
 * Render simple info block above rule rows
 * @param rootClass - typography class name
 * @param text: string content
 */
const renderInfoBlock = (rootClass: string, text: string): JSX.Element => (
    <InfoBlock>
        <InfoPointIcon />
        <Typography
            classes={{ root: rootClass }}
            align="inherit"
            bodyVariant="onPaper"
            color="initial"
            display="initial"
            variant="body1"
        >
            {text}
        </Typography>
    </InfoBlock>
);

/**
 * Render Upselling component (on screen bottom)
 * @param props - CreateSegment props
 */
const UpsellingFC = ({ isStandalone }: { isStandalone: boolean }): JSX.Element => {
    const { t } = useTranslation();
    const account = useSelector(getAccountStore);
    const { isReseller } = useSecurity();

    const paymentsUrl = getPaymentsUrl(isStandalone);
    return (
        <div style={{ marginTop: '50px' }}>
            <Upselling
                body={
                    <UpsellingBody
                        description={
                            <Typography variant="caption">
                                {t('segmenteditscreenctagetcloser')}
                                {isReseller && account?.GeneralInfo?.AccountPaymentUrl && (
                                    <>
                                        {' '}
                                        <a
                                            href={account.GeneralInfo.AccountPaymentUrl}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            {t('upgrade')}
                                        </a>{' '}
                                        {t('upgradetounlimitedcustomfields')}
                                    </>
                                )}
                                {!isReseller && (
                                    <>
                                        {' '}
                                        <a href={paymentsUrl}>{t('upgrade')}</a> {t('upgradetounlimitedcustomfields')}
                                    </>
                                )}
                            </Typography>
                        }
                        header={<Typography variant="h2">{t('doyouwanttobeclosersubscribers')}</Typography>}
                    />
                }
                containerHeight="124px"
                containerPosition="flex-end"
                icon={<ContactSvg />}
                svgBottom="0px"
                svgPosition="relative"
            />
        </div>
    );
};

/**
 * Track history toggle handler
 * @param state - CreateSegment state
 * @param setState - CreateSegment state
 */
const handleToggleTrackHistory =
    (state, setState): HandlerFunction =>
    (event, value): void => {
        setState({ ...state, trackHistory: value });
    };

/**
 * Renders Tracking Toggle to choose if we should track history for given segment
e
 */
const renderTrackingToggle = (
    segments: SegmentsState,
    lists: ListsState,
    isAllDataComplete: boolean,
    state: CreateSegmentState,
    setState: any,
    t: TFunction
): JSX.Element => {
    const historyTrackedCount =
        (segments?.historyTrackedCollection?.length || 0) + (lists?.historyTrackedCollection?.length || 0);
    const currentSegmentTrackedCount = segments.selected?.TrackHistory ? 0 : state.trackHistory ? 1 : 0;

    return (
        <>
            <TrackHistoryToggleRow>
                <TrackHistoryToggleWrapper>
                    <SimpleToggle
                        id="track-history-toggle-button"
                        disabled={!isAllDataComplete || (!state.trackHistory && historyTrackedCount >= 10)}
                        value={'track-history'}
                        onChange={handleToggleTrackHistory(state, setState)}
                        checked={state.trackHistory || false}
                    />
                </TrackHistoryToggleWrapper>
                <Typography variant="h3">{t('trackhistory')}</Typography>
                {isAllDataComplete ? (
                    <BasicBadge
                        textColor={palette.indigo[6]}
                        backgroundColor={palette.gray[1]}
                        value={`${historyTrackedCount + currentSegmentTrackedCount}/10`}
                    />
                ) : (
                    <Skeleton height="28px" width="55px" />
                )}
            </TrackHistoryToggleRow>
            <TrackHistoryToggleRow>
                <TrackHistoryToggleWrapper />
                <Typography variant="caption">{t('segmenttrackhistorydesc')}</Typography>
            </TrackHistoryToggleRow>
        </>
    );
};

/**
 * Bottom "+" button handler to add a rule on level 0
 */
const handleAddRuleClick =
    (
        SegmentClearRuleEvaluationResult: () => void,
        state: CreateSegmentState,
        setState: any
    ): MouseEventHandler<HTMLDivElement> =>
    (): void => {
        const clonedStateRule = cloneDeep(state.parsedRule);
        let operator;

        if (clonedStateRule.length) {
            // only if rule not empty
            operator = getLevelConditionOperator(clonedStateRule);
            clonedStateRule.push(operator || 'OR');
        }
        clonedStateRule.push(NEW_RULE_STRING);

        setState({
            ...state,
            parsedRule: clonedStateRule,
            rule: arrayRuleToString(clonedStateRule),
        });

        SegmentClearRuleEvaluationResult();
    };

/**
 *
 */
const handleSaveSegmentClick =
    (
        inputRefSegmentName: React.MutableRefObject<any>,
        match: any,
        SegmentUpdateEdited: (name: string, segmentPayload: SegmentPayload) => void,
        SegmentAddStart: (name: string, rule: string, trackHistory?: boolean) => void,
        ShowDialog: (params: ShowDialogParams) => void,
        state,
        setState,
        t: TFunction
    ): VoidFunction =>
    (): void => {
        const name = inputRefSegmentName?.current?.childNodes[0]?.value?.trim();
        const { rule, trackHistory } = state;

        const isEditScreen = !!match.params?.segmentname;
        const initialName = match.params?.segmentname ? safelyDecodeBase64Url(match.params?.segmentname) : '';

        if (!name) {
            setState({
                ...state,
                isSegmentNameEmptyError: true,
            });
        }

        if (name && rule) {
            if (isEditScreen) {
                if (!state.errorPositions.length) {
                    SegmentUpdateEdited(initialName, {
                        Name: name,
                        Rule: rule.replace(/\s+/g, ' '),
                        TrackHistory: trackHistory,
                    });
                } else {
                    ShowDialog({
                        title: t('missingcustomfield'),
                        cancelLabel: t('close'),
                        body: <DialogBody variant="alert">{t('missingcustomfieldinfo')}</DialogBody>,
                    });
                }
            } else {
                SegmentAddStart(name, rule.replace(/\s+/g, ' '), trackHistory);
            }
        }
    };

/**
 * Handler for rule evaluation button click ("Evaluate")

 */
const handleEvaluateClick =
    (
        ShowDialog: (params: ShowDialogParams) => void,
        SegmentRuleEvaluate: (rule: string) => void,
        state: CreateSegmentState,
        t: TFunction
    ): VoidFunction =>
    (): void => {
        const { errorPositions, rule } = state;

        if (errorPositions.length) {
            ShowDialog({
                title: t('missingcustomfield'),
                cancelLabel: t('close'),
                body: <DialogBody variant="alert">{t('missingcustomfieldinfo')}</DialogBody>,
            });
            return;
        }
        if (rule) {
            SegmentRuleEvaluate(rule.replace(/\s+/g, ' '));
        }
    };

/**
 * Translates options inside all rule row's selects
 * @param props CreateSegment props
 * @param callback - fires after performing translations
 */
const translateAllOptions = (t: TFunction): void => {
    forEach(RuleValueOptions, (categoryOptions: OptionInterface[]): void => {
        forEach(categoryOptions, (option: OptionInterface): void => {
            option.label = option.label.includes('segmentrulelabel') ? t(option.label) : option.label;
        });
    });
    forEach(ConditionOptions, (categoryOptions: OptionInterface[]): void => {
        forEach(categoryOptions, (option: OptionInterface): void => {
            option.label = option.label.includes('segmentrulelabel') ? t(option.label) : option.label;
        });
    });
    forEach(AndOrOptions, (option: OptionInterface): void => {
        option.label = option.label.includes('segmentrulelabel') ? t(option.label) : option.label;
    });
    forEach(RuleFieldOptions, (categoryOptions: RuleFieldOptionInterface[], categoryName: string): void => {
        if (categoryName.indexOf('segmentrulelabel') >= 0) {
            forEach(categoryOptions, (option: RuleFieldOptionInterface): void => {
                option.label = option.label.includes('segmentrulelabel') ? t(option.label) : option.label;
            });
            RuleFieldOptions[t(categoryName)] = cloneDeep(categoryOptions);
            delete RuleFieldOptions[categoryName];
        }
    });
};

/**
 * Add user's customfields to "Custom" category in rule FIELD select (RuleFieldOptions[t('segmentrulelabelcustom')])
 */
const appendCustomRuleFieldCategory = (customFields: CustomFieldsInterface, t: TFunction): void => {
    if (isEmpty(customFields.collection)) {
        return;
    }

    const dateCustomFieldIndexes = [];
    RuleFieldOptions[t('segmentrulelabelcustom')] = map(
        customFields.collection,
        (customField: ContactField, index): RuleFieldOptionInterface => {
            // Push index to add "Days since {fieldname}" option for each date custom field
            if (customField.Type === ContactFieldType.DateTime) {
                dateCustomFieldIndexes.push(index);
            }
            return {
                label: customField.Name.split(' ')
                    .map((word): string => word.trim().charAt(0).toUpperCase() + word.trim().slice(1))
                    .join(' '),
                value: customField.Name,
                ruleConditionType: customField.Type.toLowerCase() as RuleConditionType,
                ruleValueType: RuleConditionTypeToConditionOptions[customField.Type.toLowerCase()],
            };
        }
    );

    forEach(dateCustomFieldIndexes.reverse(), (dateFieldIndex: number): void => {
        const dateField = RuleFieldOptions[t('segmentrulelabelcustom')][dateFieldIndex];
        RuleFieldOptions[t('segmentrulelabelcustom')].splice(dateFieldIndex + 1, 0, {
            label: `${t('dayssince')} ${dateField.label}`,
            value: `daysfrom${dateField.value}`,
            ruleConditionType: 'number',
            ruleValueType: 'Number',
        });
    });
};

/**
 * Add rule VALUE options for user's listnames to RuleValueOptions.Listname
 * @param props - CreateSegment props
 */
const appendListnameToRuleValueOptions = (lists: ListsState): void => {
    RuleValueOptions.Listname = map(
        lists.collection,
        (list: ContactsList): OptionInterface => ({
            label: list.ListName,
            value: list.ListName,
        })
    );
};

/**
 * To be used only on EDIT segment screen (not create segment)
 * Gets segment name from url, finds segment in story by name, sets state.currentSegment
 * @param props - CreateSegment props
 * @param setState - CreateSegment setState
 */
const setCurrentSegment = (segments: SegmentsState, setState: any): void => {
    const currentSegment = cloneDeep(segments.selected);

    setState(
        (state: CreateSegmentState): CreateSegmentState => ({
            ...state,
            rule: currentSegment.Rule.replace(/\s+/g, ' '),
            parsedRule: parseStringRule(currentSegment.Rule.replace(/\s+/g, ' ')),
            trackHistory: currentSegment.TrackHistory,
        })
    );
};

const checkSegmentNameErrors = (segments: SegmentsState, state: any, segmentName: string, setState): void => {
    const isEmptySegmentName = segmentName.trim().length === 0;
    const isSegmentNameDuplicated = !!find(
        segments.segmentNamesCollection,
        (foundSegment: Segment): boolean => foundSegment.Name === segmentName
    );
    if (
        state.isSegmentNameEmptyError !== isEmptySegmentName ||
        state.isSegmentNameDuplicated !== isSegmentNameDuplicated
    ) {
        setState({
            ...state,
            isSegmentNameEmptyError: isEmptySegmentName,
            isSegmentNameDuplicated,
        });
    }
};

const parseName = (name: string): string =>
    name
        .split(/(?=[A-Z])/)
        .map((name: string): string => name.toLowerCase())
        .join('');

const getDefaultState = (isEditScreen: boolean): any => ({
    rule: isEditScreen ? null : NEW_RULE_STRING,
    parsedRule: parseStringRule(NEW_RULE_STRING),
    trackHistory: isEditScreen ? null : false,
    isOptionsTranslated: false,
    errorPositions: [],
    isSegmentNameEmptyError: false,
    isSegmentNameDuplicated: false,
});

const prepareSegmentOptions = (customFields: CustomFieldsInterface, lists: ListsState, t: TFunction): void => {
    if (customFields.collection && !RuleFieldOptions[t('segmentrulelabelcustom')]) {
        appendCustomRuleFieldCategory(customFields, t);
    }
    if (lists.collection && !RuleValueOptions.Listname) {
        appendListnameToRuleValueOptions(lists);
    }
};

const formatRuleEvaluation = (ruleEvaluationResult) =>
    map(
        ruleEvaluationResult,
        (
            count,
            name
        ): {
            name: string;
            count: number;
        } => ({ name, count })
    );

//we are removing year from month-day option for date
function convertRuleToDisplay(input: string): string {
    const monthDayRegex = /month-day '[0-9]{4}-[0-9]{2}-[0-9]{2}'/i;
    const dateWithoutYear = monthDayRegex.exec(input)?.[0]?.replace(/[0-9]{4}-/i, '');
    if (!dateWithoutYear) {
        return input;
    }
    return input?.replace(monthDayRegex, dateWithoutYear);
}

const CreateSegment: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const classes = useStyles();
    const match = useRouteMatch();
    const dispatch = useDispatch();
    const { isProPlan } = useSecurity();
    const inputRefSegmentName = useRef(null);
    useInitData();

    const { lists, segments, customFields, config } = useSelector(getDataFromStore);
    const { segmentname = '' } = match.params as any;
    const isEditScreen = !isEmpty(segmentname);
    const initialName = segmentname;

    const [state, setState] = useState(getDefaultState(isEditScreen));

    translateAllOptions(t);
    prepareSegmentOptions(customFields, lists, t);

    const isAllDataComplete =
        isArray(segments.historyTrackedCollection) &&
        isArray(lists.collection) &&
        isArray(lists.historyTrackedCollection) &&
        isArray(customFields.collection) &&
        ((isEditScreen && !!segments.selected) || !isEditScreen);

    const mappedRuleEvaluationResults = formatRuleEvaluation(segments.ruleEvaluationResult);

    const filteredRuleEvaluationResults = segments.ruleEvaluationResult
        ? filter(mappedRuleEvaluationResults, (result): boolean => result.count > 0 || result.name === 'total')
        : null;

    if (isEditScreen && state.rule === null && segments.selected) {
        setCurrentSegment(segments, setState);
    }

    const rows = isAllDataComplete ? (
        ruleRows(state.parsedRule, setState, classes.andOrRadio, state)
    ) : (
        <Skeleton height="44px" width="100%" />
    );

    const showDialog = (params: ShowDialogParams): void => {
        dispatch(ShowDialog(params));
    };
    const segmentRuleEvaluate = (rule: string): void => {
        dispatch(SegmentRuleEvaluate(rule));
    };
    const segmentClearRuleEvaluationResult = (): void => {
        dispatch(SegmentClearRuleEvaluationResult());
    };
    const segmentUpdateEdited = (name: string, segmentPayload: SegmentPayload): void => {
        dispatch(SegmentUpdateEdited(name, segmentPayload));
    };
    const segmentAddStart = (name: string, rule: string, trackHistory = false): void => {
        dispatch(SegmentAddStart(name, rule, trackHistory));
    };
    const Wrapper = config.isStandalone ? Container : Fragment;
    return (
        <Wrapper>
            <TabsPanel
                title={isEditScreen ? t('editsegment') : t('createsegment')}
                additionalHeaderContent={
                    <PrimaryButton
                        id={generateIdBasedOnPath('save-segment-button')}
                        text={t('savesegment')}
                        disabled={state.isSegmentNameEmptyError || state.isSegmentNameDuplicated || !state.rule}
                        onClick={handleSaveSegmentClick(
                            inputRefSegmentName,
                            match,
                            segmentUpdateEdited,
                            segmentAddStart,
                            showDialog,
                            state,
                            setState,
                            t
                        )}
                    />
                }
                tabBackground={'#fff'}
                isMobile={false}
                breadcrumbs={t('segments')}
                breadcrumbsOnClick={(): void => {
                    dispatch(push(ContactsPaths.Segments));
                }}
            />
            <Paper classes={{ root: classes.paperRoot }} elevation={0} className="segmentName">
                <BasicInput
                    ref={inputRefSegmentName}
                    defaultValue={safelyDecodeBase64Url(initialName)}
                    id="new-segment-name-input"
                    name="input__new-segment-name"
                    label={t('segmentname')}
                    placeholder={t('segmentname')}
                    onChange={(e): void => {
                        checkSegmentNameErrors(segments, state, e.currentTarget.value, setState);
                    }}
                    type="text"
                    error={state.isSegmentNameEmptyError || state.isSegmentNameDuplicated}
                    errorMessage={
                        state.isSegmentNameDuplicated ? t('segmentnamealreadyexist') : t('mustprovidesegmentname')
                    }
                />
            </Paper>
            <Paper classes={{ root: classes.paperRoot }} elevation={0}>
                {renderInfoBlock(classes.ruleNotificationTypograpyRoot, t('andorsegmentrulenotification'))}
                <RulesWrapper>{rows}</RulesWrapper>
                <AddRuleRow>
                    <AddRuleBtn
                        id="add-rule-button"
                        onClick={handleAddRuleClick(segmentClearRuleEvaluationResult, state, setState)}
                    >
                        <AddBluePulseIcon />
                    </AddRuleBtn>
                </AddRuleRow>
                <RuleWrapper>
                    <Typography classes={{ root: classes.ruleTypographyRoot }} variant="h4">
                        {t('rule')}:
                    </Typography>
                    &nbsp;
                    <RuleSpan>{`${convertRuleToDisplay(state.rule)}`}</RuleSpan>
                </RuleWrapper>
                <EvaluateWrapper>
                    <TertiaryButton
                        id="evaluate-button"
                        text={t('evaluate')}
                        onClick={handleEvaluateClick(showDialog, segmentRuleEvaluate, state, t)}
                    />
                    {filteredRuleEvaluationResults
                        ? map(
                              filteredRuleEvaluationResults,
                              (result): JSX.Element => (
                                  <RuleEvaluationResultSpan key={`evaluation-badge-${result.name}-${result.count}`}>
                                      {t(parseName(result.name))}:&nbsp;
                                      <BasicBadge value={result.count} variant="Status name" />
                                  </RuleEvaluationResultSpan>
                              )
                          )
                        : ''}
                </EvaluateWrapper>
            </Paper>
            <Paper classes={{ root: classes.paperRoot }} elevation={0}>
                {renderTrackingToggle(segments, lists, isAllDataComplete, state, setState, t)}
            </Paper>
            {!isProPlan && <UpsellingFC isStandalone={config?.isStandalone} />}
        </Wrapper>
    );
};

export default CreateSegment;
